import React, { createContext, useContext, useState, useEffect } from 'react';
// import { useTranslation } from 'react-i18next';
import { setSentryUser, addBreadcrumb, captureException } from '../services/sentry';
import { login as apiLogin } from '../services/ApiService';

interface AuthContextType {
  isLoggedIn: boolean;
  setIsLoggedIn: (value: boolean) => void;
  login: (username: string, password: string, newPassword?: string) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  // const { i18n } = useTranslation();

  useEffect(() => {
    // Check if user is already logged in
    const token = localStorage.getItem('accessToken');
    if (token) {
      setIsLoggedIn(true);
      // Set user context in Sentry if we have user info
      const userName = localStorage.getItem('userName');
      if (userName) {
        setSentryUser(userName, userName, undefined);
      }
    }
  }, []);

  const login = async (username: string, password: string, newPassword?: string) => {
    try {
      addBreadcrumb('Attempting login', 'auth', 'info', { username });
      const response = await apiLogin(username, password, newPassword);
      
      setIsLoggedIn(true);
      localStorage.setItem('accessToken', response.accessToken);
      localStorage.setItem('refreshToken', response.refreshToken);
      localStorage.setItem('userName', username);
      if (response['custom:role']) {
        localStorage.setItem('custom:role', response['custom:role']);
      }

      // Set user context in Sentry
      setSentryUser(username, username, undefined);
      addBreadcrumb('Login successful', 'auth', 'info', { username });
    } catch (error) {
      captureException(error instanceof Error ? error : new Error(String(error)), {
        tags: {
          source: 'AuthContext',
          action: 'login',
        },
      });
      throw error;
    }
  };

  const logout = () => {
    setIsLoggedIn(false);
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('userName');
    localStorage.removeItem('custom:role');
    setSentryUser(null);
    addBreadcrumb('User logged out', 'auth', 'info');
  };

  return (
    <AuthContext.Provider value={{ isLoggedIn, setIsLoggedIn, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};